/**
 * This makes use of react-toastify (https://fkhadra.github.io/react-toastify/introduction)
 * as the sole manager of notifications
 */
import 'react-toastify/dist/ReactToastify.css';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect} from 'react';
import {
  Slide,
  ToastContainer as ToastContainerComponent,
  toast,
} from 'react-toastify';
import {connect, useSelector} from 'react-redux';
import {isPlaneteer} from '@apps-common/auth-utils';
import {styled} from '@mui/material/styles';
import {useAuth} from '@apps-common/auth-react';

import Notification from './notification.jsx';
import OutOfTilesNotice from './out-of-tiles.jsx';
import {dismissNotification} from '@explorer/actions/notifications';
import {showNoQuotaBanner} from '@explorer/selectors/tile-groups.js';
import {darkTheme as theme} from '@explorer/styles';

export const NOTIFICATION_TYPES = {
  snackbar: 'snackbar',
  miniDialog: 'miniDialog',
};

const ToastContainer = styled(ToastContainerComponent)(
  ({noQuotaBannerShown}) => ({
    '&.Toastify__toast-container': {
      width: 'auto',
      maxWidth: '100%',
      padding: 0,
      marginTop: theme.spacing(1),
      [theme.breakpoints.down('md')]: {
        minWidth: `calc(100% - ${theme.spacing(14)})`,
        transform: 'none',
        left: theme.spacing(7),
      },
      zIndex: theme.zIndex.modal + 1,
    },
    '&.Toastify__toast-container--top-center': {
      top: noQuotaBannerShown
        ? theme.mixins.topNav.height * 2
        : theme.mixins.topNav.height,
    },
    '& .Toastify__toast': {
      padding: 0,
      background: 'none',
      minHeight: 0,
      boxShadow: 'none',
    },
  })
);

const Notifier = ({notification}) => {
  const auth = useAuth();
  const noQuotaBannerShown = useSelector(state => showNoQuotaBanner(state));
  /**
   * This is what truly triggers notification to queued
   * and shown via react-toastify.
   */
  const onShowNotification = useCallback(() => {
    if (notification) {
      const {persist, position, notificationClass, ...notificationProps} =
        notification;

      const altKey =
        typeof notification.message === 'string'
          ? notification.message
          : `${new Date().getTime() + Math.random()}`;

      const toastId = notification.key || altKey;

      const NotifierContent = <Notification {...notificationProps} />;

      toast(NotifierContent, {
        toastId,
        autoClose: persist ? false : 5000,
        position: position,
      });
    }
  }, [notification]);

  useEffect(onShowNotification, [notification, onShowNotification]);

  return (
    <React.Fragment>
      <ToastContainer
        hideProgressBar
        closeButton={false}
        closeOnClick={false}
        position={toast.POSITION.TOP_CENTER}
        transition={Slide}
        noQuotaBannerShown={
          !isPlaneteer(auth.user.claims.email) && noQuotaBannerShown
        }
      />
      <OutOfTilesNotice />
    </React.Fragment>
  );
};

Notifier.propTypes = {
  notification: PropTypes.object,
  dismissNotification: PropTypes.func,
};

const mapStateToProps = state => ({
  notification: state.notifications.notification,
});

const mapDispatchProps = {
  dismissNotification,
};

export default connect(mapStateToProps, mapDispatchProps)(Notifier);
